// components/ScraperDashboard/AdvancedSection.js
// Collapsible wrapper for scraper dashboard cards / sections.
import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import './AdvancedSection.css';

const LS_PREFIX = 'scraper-dashboard:collapsed:';

/**
 * AdvancedSection - Collapsible wrapper used by every top-level card in the
 * scraper dashboard. When `storageKey` is provided, expanded/collapsed state
 * is persisted to localStorage under `scraper-dashboard:collapsed:<key>` so
 * operator preferences survive page refreshes.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Content to render when expanded
 * @param {string} props.title - Section title
 * @param {React.ReactNode} props.icon - Icon component
 * @param {string} props.description - Brief description shown when collapsed
 * @param {string} props.badge - Optional badge text (e.g., count)
 * @param {boolean} props.defaultExpanded - Whether to expand by default (ignored if a saved value exists for storageKey)
 * @param {string} props.storageKey - Persists collapse state to localStorage under this key
 */
function AdvancedSection({
  children,
  title,
  icon: Icon,
  description,
  badge,
  defaultExpanded = false,
  storageKey,
}) {
  // Initialize from localStorage if a storageKey was provided, else use default.
  const [expanded, setExpanded] = useState(() => {
    if (!storageKey || typeof window === 'undefined') return defaultExpanded;
    try {
      const raw = window.localStorage.getItem(LS_PREFIX + storageKey);
      if (raw === 'true') return true;
      if (raw === 'false') return false;
    } catch (_) { /* localStorage may be unavailable in some contexts */ }
    return defaultExpanded;
  });

  // Persist on change.
  useEffect(() => {
    if (!storageKey || typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(LS_PREFIX + storageKey, String(expanded));
    } catch (_) { /* swallow — localStorage full / blocked */ }
  }, [expanded, storageKey]);

  return (
    <div className={`ads-container ${expanded ? 'ads-expanded' : ''}`}>
      <div className="ads-header" onClick={() => setExpanded(!expanded)}>
        <div className="ads-header-left">
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          {Icon && <Icon size={16} className="ads-icon" />}
          <span className="ads-title">{title}</span>
          {badge && <span className="ads-badge">{badge}</span>}
        </div>
        {!expanded && description && (
          <span className="ads-description">{description}</span>
        )}
      </div>

      {expanded && (
        <div className="ads-content">
          {children}
        </div>
      )}
    </div>
  );
}

export default AdvancedSection;
